/**
 * How long the module keeps what it keeps.
 *
 * ⚠⚠ THE ONLY CONTROL IN THIS PRODUCT THAT DELETES ON A TIMER. Everything else
 * on this page decides whether letters go out; this decides whether data stays.
 * So the card does two things a settings form normally would not: it shows what
 * turning the switch on would remove BEFORE it is on, and it re-asks that
 * question against the numbers currently in the boxes rather than the numbers
 * currently stored. Somebody about to shorten the window from two years to one
 * month is exactly the person that count is for, and answering them with the
 * old window's figure would read as reassurance.
 *
 * ⚠ THE CONTACT AGE MAY NOT BE THE AGE IN THE BOX. Deleting an address memory
 * deletes `lastContactedAt`, which is the only thing the cross-campaign cooldown
 * reads - so an age shorter than the longest re-contact window would let the
 * sender write to somebody it wrote to last month. The server clamps it, and the
 * card says so and says what it is clamped against. A limit whose reason is
 * invisible reads as a bug.
 */
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Archive, Loader2, Save } from "lucide-react";
import { Button, Input, Switch } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import {
  previewRetention,
  saveRetention,
  type RetentionDue,
  type RetentionSettings,
  type RetentionView,
} from "../../../../lib/marketingRetention";
import { Field, Note, Panel, Stat, num } from "../shared";

/** Clamp a typed number to the range the server accepts, so the form cannot
 *  offer a value that comes back as a 400. */
function bounded(raw: string, min: number, max: number, fallback: number): number {
  const n = Number(raw.replace(/\s/g, ""));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

export function RetentionCard({
  view,
  canManage,
  onChanged,
}: {
  view: RetentionView;
  canManage: boolean;
  onChanged: () => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState<RetentionSettings>(view.settings);
  const [due, setDue] = useState<RetentionDue>(view.due);
  const [busy, setBusy] = useState(false);

  const dirty = JSON.stringify(draft) !== JSON.stringify(view.settings);
  const set = (patch: Partial<RetentionSettings>) => setDraft((d) => ({ ...d, ...patch }));

  const { recipientDays, contactDays } = draft;
  const refreshPreview = useCallback(() => {
    previewRetention({ recipientDays, contactDays }).then((r) => setDue(r.due)).catch(() => {});
  }, [recipientDays, contactDays]);

  // Debounced, because it runs three counts on the ledger and the operator is
  // typing. Half a second is long enough that "730" is one question and short
  // enough that the answer arrives before they look away.
  useEffect(() => {
    const timer = setTimeout(refreshPreview, 500);
    return () => clearTimeout(timer);
  }, [refreshPreview]);

  async function save() {
    setBusy(true);
    try {
      const res = await saveRetention(draft);
      setDue(res.due);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title={t("marketing.retention.title")}
      description={t("marketing.retention.intro")}
      icon={<Archive className="size-4" />}
    >
      {/* Above the switch, not below it. This is what the switch does, and a
          warning under a control is a warning read after the decision. */}
      <Note tone={draft.enabled ? "warn" : "info"} icon={<AlertTriangle className="size-3.5" />} className="mb-4">
        {t("marketing.retention.warning")}
      </Note>

      <label className="flex items-start gap-2.5 text-sm">
        <Switch
          checked={draft.enabled}
          onCheckedChange={(v) => set({ enabled: v })}
          disabled={!canManage || busy}
          className="mt-0.5"
        />
        <span>
          {t("marketing.retention.enabled")}
          <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
            {t("marketing.retention.enabledHint")}
          </span>
        </span>
      </label>

      <div className="mt-4 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        <Field label={t("marketing.retention.recipientDays")} hint={t("marketing.retention.recipientHint")}>
          <Input
            value={String(draft.recipientDays)}
            onChange={(e) =>
              set({ recipientDays: bounded(e.target.value, view.limits.min, view.limits.max, draft.recipientDays) })
            }
            inputMode="numeric"
            disabled={!canManage || busy}
          />
        </Field>
        <Field label={t("marketing.retention.contactDays")} hint={t("marketing.retention.contactHint")}>
          <Input
            value={String(draft.contactDays)}
            onChange={(e) =>
              set({ contactDays: bounded(e.target.value, view.limits.min, view.limits.max, draft.contactDays) })
            }
            inputMode="numeric"
            disabled={!canManage || busy}
          />
        </Field>
      </div>

      {due.clamped ? (
        <Note tone="warn" icon={<AlertTriangle className="size-3.5" />} className="mt-3.5">
          {t("marketing.retention.clamped", {
            typed: num(draft.contactDays),
            inForce: num(due.contactDaysInForce),
            cooldown: num(view.longestRecontactDays),
          })}
        </Note>
      ) : null}

      <div className="mt-4 grid grid-cols-3 gap-3 border-t border-border pt-4">
        <Stat
          label={t("marketing.retention.due.recipients")}
          value={num(due.recipients)}
          sub={t("marketing.retention.due.recipientsSub")}
          tone={due.recipients > 0 ? "warn" : "muted"}
        />
        <Stat
          label={t("marketing.retention.due.contacts")}
          value={num(due.contacts)}
          sub={t("marketing.retention.due.contactsSub")}
          tone={due.contacts > 0 ? "warn" : "muted"}
        />
        <Stat
          label={t("marketing.retention.due.threads")}
          value={num(due.threads)}
          sub={t("marketing.retention.due.threadsSub")}
          tone={due.threads > 0 ? "warn" : "muted"}
        />
      </div>

      <p className="crm-sub mt-3">{t("marketing.retention.kept")}</p>

      <div className="mt-4 flex items-center gap-3">
        <Button onClick={() => void save()} disabled={!canManage || busy || !dirty}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          {t("common.save")}
        </Button>
        {!canManage ? <span className="crm-sub">{t("marketing.retention.readOnly")}</span> : null}
      </div>
    </Panel>
  );
}
